/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text, Title, StatusBadge } from '@/shared/ui';
import { translateKnownSchemaItem } from '@/features/property-management/components/PropertyFormDualPane/schema.constants';
import { PublicProperty, getCategoryDisplay, formatBRL } from './PropertyPublicView.types';
import { StatCell, Chip } from './PropertyPublicView.atoms';

/**
 * Small price tile for the public listing, rendering an uppercase label above
 * a formatted price value with an optional suffix (e.g. "/month"). Compact
 * mode reduces padding and type size for secondary prices like condo fees.
 *
 * @param props.label - Uppercase caption for the price.
 * @param props.value - Formatted price string.
 * @param props.suffix - Optional trailing unit (e.g. per-month).
 * @param props.compact - Whether to render the smaller variant.
 * @returns The price card element.
 */
function PriceCard({
  label,
  value,
  suffix,
  compact,
}: {
  label: string;
  value: string;
  suffix?: string;
  compact?: boolean;
}) {
  return (
    <div
      className={
        compact
          ? 'rounded-(--radius-md) bg-(--surface-secondary) px-(--space-3) py-(--space-2) shadow-[var(--shadow-sm)]'
          : 'rounded-(--radius-lg) bg-(--surface-secondary) px-(--space-4) py-(--space-3) shadow-[var(--shadow-sm)]'
      }
      data-testid="public-property-price"
    >
      <Text
        as="span"
        className="block text-[length:var(--text-meta)] font-semibold tracking-wide text-(--text-tertiary) uppercase"
      >
        {label}
      </Text>
      <Text
        className={
          compact
            ? 'mt-(--space-0-5) text-[length:var(--text-body-sm)] font-semibold text-(--text-secondary)'
            : 'mt-(--space-1) text-[length:var(--text-app-section)] font-bold text-(--seed-base)'
        }
      >
        {value}
        {suffix && (
          <Text variant="meta" as="span" className="font-normal text-(--text-tertiary)">
            {suffix}
          </Text>
        )}
      </Text>
    </div>
  );
}

/**
 * Titled group of amenity chips (e.g. property/condominium/security items),
 * mapping each raw value through the provided label resolver.
 *
 * @param props.title - Uppercase group heading.
 * @param props.items - Raw amenity values to render as chips.
 * @param props.labelOf - Resolves a raw value to a display label.
 * @returns The amenity group element.
 */
function AmenityGroup({ title, items, labelOf }: { title: string; items: string[]; labelOf: (v: string) => string }) {
  return (
    <div>
      <Text
        as="span"
        className="mb-(--space-2) block text-[length:var(--text-meta)] font-semibold tracking-wide text-(--text-tertiary) uppercase"
      >
        {title}
      </Text>
      <div className="flex flex-wrap gap-(--space-2)">
        {items.map((a) => (
          <Chip key={a} label={labelOf(a)} />
        ))}
      </div>
    </div>
  );
}

/**
 * Title, prices, stats, description and amenities for the public listing.
 * Derives sale/rental/condo prices, housing stats, and amenity groups from the
 * property and localizes categories/labels; hides sections with no data.
 *
 * @param props.property - The public property to render.
 * @returns The property info panel.
 */
export function PropertyInfoPanel({ property }: { property: PublicProperty }) {
  const { t } = useTranslation('@pages/public-property');
  const { t: tProp } = useTranslation('features/properties');
  const salePrice = property.sale?.price;
  const rentalPrice = property.rental?.price;
  const bedrooms = property.housing?.bedrooms;
  const suites = property.housing?.suites;
  const condoPrice = property.condominium?.price;
  const propertyAmenities = [...(property.leisure_areas || []), ...(property.facilities || [])];
  const securityItems = [...(property.security || []), ...(property.condominium?.security || [])];
  const condoAmenities = property.condominium?.leisure_areas || [];
  const location = [property.neighborhood, property.city, property.state].filter(Boolean).join(', ');
  const categoryDisplay = getCategoryDisplay(t)[property.category || ''] || property.category;
  const labelOf = (value: string) => translateKnownSchemaItem(tProp, value);
  const hasAmenities =
    propertyAmenities.length > 0 || condoAmenities.length > 0 || securityItems.length > 0;

  return (
    <div className="flex max-h-[calc(100vh-113px)] flex-col gap-(--space-4) overflow-y-auto pr-(--space-2)">
      {categoryDisplay && <StatusBadge tone="warning">{categoryDisplay}</StatusBadge>}

      <div>
        <Title order={1} className="m-0 text-[length:var(--text-app-section)] font-bold tracking-tight text-(--text-primary)">
          {property.property_title}
        </Title>
        <Text variant="bodySm" className="mt-(--space-1) text-(--text-secondary)">{location}</Text>
      </div>

      {(property.transaction_type || []).length > 0 && (
        <div className="flex flex-wrap gap-(--space-2)">
          {(property.transaction_type || []).map((tt) => (
            <StatusBadge key={tt} tone="info">
              {tt}
            </StatusBadge>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-(--space-3)">
        {salePrice && <PriceCard label={t('price.sale')} value={formatBRL(salePrice)} />}
        {rentalPrice && (
          <PriceCard label={t('price.rental')} value={formatBRL(rentalPrice)} suffix={t('price.perMonth')} />
        )}
        {condoPrice && (
          <PriceCard
            label={t('price.condominium')}
            value={formatBRL(condoPrice)}
            suffix={t('price.perMonth')}
            compact
          />
        )}
      </div>

      <div className="grid grid-cols-3 gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-secondary) p-(--space-3) shadow-[var(--shadow-sm)]">
        {property.total_area && <StatCell value={`${property.total_area}`} unit="m²" label={t('stats.totalArea')} />}
        {property.constructed_area && (
          <StatCell value={`${property.constructed_area}`} unit="m²" label={t('stats.constructedArea')} />
        )}
        {bedrooms !== undefined && <StatCell value={`${bedrooms}`} unit="" label={t('stats.bedrooms')} />}
        {suites !== undefined && suites > 0 && <StatCell value={`${suites}`} unit="" label={t('stats.suites')} />}
        {property.bathrooms !== undefined && (
          <StatCell value={`${property.bathrooms}`} unit="" label={t('stats.bathrooms')} />
        )}
        {property.parking_spots !== undefined && (
          <StatCell value={`${property.parking_spots}`} unit="" label={t('stats.parking')} />
        )}
      </div>

      {property.description && (
        <div className="rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-secondary) p-(--space-4) shadow-[var(--shadow-sm)]">
          <Title order={3} className="mb-(--space-2) text-[length:var(--text-body-sm)] font-semibold text-(--text-primary)">
            {t('description')}
          </Title>
          <Text variant="bodySm" className="m-0 leading-relaxed text-(--text-secondary)">
            {property.description}
          </Text>
        </div>
      )}

      {hasAmenities && (
        <div className="rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-secondary) p-(--space-4) shadow-[var(--shadow-sm)]">
          <Title order={3} className="mb-(--space-3) text-[length:var(--text-body-sm)] font-semibold text-(--text-primary)">
            {t('amenities.title')}
          </Title>
          <div className="flex flex-col gap-(--space-3)">
            {propertyAmenities.length > 0 && (
              <AmenityGroup title={t('amenities.property')} items={propertyAmenities} labelOf={labelOf} />
            )}
            {condoAmenities.length > 0 && (
              <AmenityGroup title={t('amenities.condominium')} items={condoAmenities} labelOf={labelOf} />
            )}
            {securityItems.length > 0 && (
              <AmenityGroup title={t('amenities.security')} items={securityItems} labelOf={labelOf} />
            )}
          </div>
        </div>
      )}

      <footer className="mt-(--space-1) border-t border-(--border-subtle) pt-(--space-3)">
        <Text
          className="m-0 text-[length:var(--text-meta)] text-(--text-tertiary)"
          dangerouslySetInnerHTML={{ __html: t('footer') }}
        />
      </footer>
    </div>
  );
}
